import { useBroadcast } from '@/composables/broadcast'
import { EVENT_LOGGED_OUT } from '@/constants/broadcast'
import { getHash } from '@/helpers/strings'
import { HttpError } from '@/helpers/errors'
import { isMultiEntityRole } from '@/helpers/user'
import { useUserStore } from '@/stores/user'
import ApiService from '@/services/api.service'
import { currentDateInUTCString } from '@/services/date.service'
import TokenService from '@/services/user/token'

import type { AxiosResponse } from 'axios'
import type { CustomAxiosRequestConfig } from '@/services/api.service'
import type {
  EmptyDataResponse,
  SuccessResponse,
} from '@/types/services/common'
import type {
  CurrentUser,
  GeneralResponse,
  LoginConfig,
  LoginResponse,
  ProfileResponse,
  RefreshTokenResponse,
  RegisterAuthData,
  RegisterAuthResponse,
  RegisterData,
  SessionRole,
  Token,
  UpdateProfileData,
  UserSession,
  Verify2faResponse,
} from '@/types/services/user'

const CURRENT_ENTITY_KEY = 'current_entity'
const CURRENCT_ROLE_KEY = 'current_role'
const LAST_VISIBLE_ENTITY_KEY = 'last_visible_entity_id'
const LAST_VISIBLE_ROLE_KEY = 'last_visible_role'
const AUTH_ERROR_KEY = 'auth_errors'
const LAST_ASSISTANT_CLIENT_KEY = 'last_assistant_client_relation_id'

function authorize_user(token: Token) {
  TokenService.saveAuthToken(token.Authorization)
  TokenService.saveRefreshToken(token.refresh_token)
  TokenService.updateRefreshTokenExpiration(token.refresh_token_expiration)
  ApiService.setAuthHeader()
  ApiService.mount401Interceptor()
}

class UserService {
  /**
   * Load user
   **/
  static getMe(): Promise<AxiosResponse<CurrentUser>> {
    try {
      return ApiService.get('/api/v3/users/me')
    } catch (error) {
      throw new HttpError(error)
    }
  }

  /**
   * Load general info
   **/
  static getGeneral(): Promise<AxiosResponse<GeneralResponse>> {
    try {
      return ApiService.get('/api/v2/root/general')
    } catch (error) {
      throw new HttpError(error)
    }
  }

  /**
   * Login the user. If 2FA is enabled for this user, the response contains
   * `requires_2fa: true` and a `challenge_id` — no token is stored; the caller
   * must then call `/api/v3/users/login/verify-2fa` with the TOTP code.
   * Otherwise, the access token is stored immediately.
   *
   * When the captcha is enabled for this environment (see
   * `getLoginConfig`), a visible Turnstile widget runs on the login form
   * and `captchaToken` carries its solved token; the backend refuses a
   * missing/invalid token on every non-whitelisted login.
   **/
  static async login(
    email: string,
    password: string,
    captchaToken?: string
  ): Promise<LoginResponse> {
    try {
      const response = (await ApiService.post(
        '/api/v3/users/login',
        {
          email,
          password,
          captcha_token: captchaToken,
        },
        { skip401Interceptor: true }
      )) as AxiosResponse<LoginResponse>

      if (response.data.token) {
        authorize_user(response.data.token)
      }

      return response.data
    } catch (error) {
      throw new HttpError(error)
    }
  }

  /**
   * Whether the login captcha is enabled for this environment — public
   * endpoint, read before any authentication. One ansible setting
   * (CAPTCHA_ENABLED on the backend) drives both the server-side gate and
   * the widget here. Callers must fail CLOSED on a fetch error (assume
   * enabled): the server may be enforcing, and a widget-less form would
   * strand the user at a tokenless login the backend refuses.
   */
  static async getLoginConfig(): Promise<LoginConfig> {
    const response = (await ApiService.get(
      '/api/v2/root/login-config'
    )) as AxiosResponse<LoginConfig>
    return response.data
  }

  /**
   * Exchange a 2FA challenge + code for a token (used after `login` returned
   * `requires_2fa: true`). Stores the token on success.
   */
  static async verifyLogin2fa(
    challenge_id: string,
    code: string
  ): Promise<Verify2faResponse> {
    try {
      const response = (await ApiService.post(
        '/api/v3/users/login/verify-2fa',
        { challenge_id, code },
        { skip401Interceptor: true }
      )) as AxiosResponse<Verify2faResponse>

      authorize_user(response.data.token)

      return response.data
    } catch (error) {
      throw new HttpError(error)
    }
  }

  /**
   * Register new user
   **/
  static async registerUser(data: RegisterData): Promise<SuccessResponse> {
    try {
      ApiService.unmount401Interceptor()

      const response = await ApiService.post('/api/v3/users/register', data)
      return response.data
    } catch (error) {
      throw new HttpError(error)
    } finally {
      ApiService.mount401Interceptor()
    }
  }

  static async registerAuthUser(
    data: RegisterAuthData
  ): Promise<RegisterAuthResponse> {
    try {
      const response = await ApiService.post('/api/v1/users/register_auth', {
        ...data,
        request_timestamp: currentDateInUTCString(),
      })
      return response.data
    } catch (error) {
      throw new HttpError(error)
    }
  }

  /**
   * Change password
   *
   * @param old_password hashed current password
   * @param new_password hashed new password
   */
  static changePassword(
    old_password: string,
    new_password: string
  ): Promise<AxiosResponse<SuccessResponse>> {
    return ApiService.put('/api/v3/users/password', {
      old_password,
      new_password,
    })
  }

  /**
   * Update user's profile
   */
  static async updateProfile(
    data: UpdateProfileData
  ): Promise<ProfileResponse> {
    const response = await ApiService.patch('/api/v3/users/me', data)
    return response.data
  }

  /**
   * Refresh the access token.
   **/
  static async refreshToken(): Promise<string> {
    const refreshToken = TokenService.getRefreshToken()

    const requestData: CustomAxiosRequestConfig = {
      method: 'post',
      url: ApiService.getResourceUrl('/api/v3/users/token/refresh'),
      skip401Interceptor: true,
      data: {
        refresh_token: refreshToken,
      },
    }

    const response = (await ApiService.customRequest(
      requestData
    )) as AxiosResponse<RefreshTokenResponse>
    TokenService.saveAuthToken(response.data.Authorization)
    // Update the header in ApiService
    ApiService.setAuthHeader()

    return response.data.Authorization
  }

  /**
   * Logout the current user by removing the token from storage.
   *
   * Will also remove `Authorization Bearer <token>` header from future requests.
   **/
  static async logout(
    urlParams = '',
    redirect = true,
    endSession = true
  ): Promise<void> {
    // Remove the token and remove Authorization header from Api Service as well
    const userStore = useUserStore()
    const broadcast = useBroadcast()
    try {
      if (endSession && TokenService.getAuthToken()) {
        await ApiService.post('/api/v3/users/logout', undefined, {
          skip401Interceptor: true,
        })
        userStore.is_logging_out = true
      }
    } catch (err) {
      console.debug(err)
      console.warn('Something went wrong when logging out. Session not ended.')
    } finally {
      TokenService.removeAuthToken()
      TokenService.removeRefreshToken()
      TokenService.removeRefreshTokenExpiration()
      ApiService.removeApiHeaders()
      ApiService.unmount401Interceptor()
      sessionStorage.removeItem(CURRENT_ENTITY_KEY)
      sessionStorage.removeItem(CURRENCT_ROLE_KEY)

      if (redirect) {
        window.location.href = '/signin' + urlParams
      } else {
        userStore.is_logging_out = false
        sessionStorage.setItem('logout_initiated_by_me', 'true')
      }

      broadcast.emit(EVENT_LOGGED_OUT)
    }
  }

  /**
   * Sends the token received from a confirmation mail
   */
  static async confirmEmail(
    token: string
  ): Promise<AxiosResponse<SuccessResponse>> {
    ApiService.unmount401Interceptor()
    try {
      return await ApiService.put('/api/v3/users/email/verify', {
        token_hash: token,
      })
    } catch (error) {
      throw new HttpError(error)
    } finally {
      ApiService.mount401Interceptor()
    }
  }

  /**
   * Request new verification email
   */
  static resendVerificationEmail(
    email: string
  ): Promise<AxiosResponse<SuccessResponse>> {
    return ApiService.post('/api/v3/users/email/verify', { email })
  }

  static changeUserLoginEmail(
    user_id: string,
    new_email: string
  ): Promise<AxiosResponse<EmptyDataResponse>> {
    return ApiService.post(`/api/v1/users/change_login_email`, {
      user_id,
      new_email,
    })
  }

  static hashPassword(password: string, email: string): string {
    return getHash(`${password}||${email}`)
  }

  static getFromUrlParamsEntityId(): string | null {
    const searchParams = new URLSearchParams(window.location.search)
    return searchParams.get('entity_id')
  }

  static setCurrentSession(role: SessionRole, entity_id?: string | null) {
    sessionStorage.setItem(CURRENCT_ROLE_KEY, role)
    if (entity_id) {
      sessionStorage.setItem(CURRENT_ENTITY_KEY, entity_id)
    } else {
      sessionStorage.removeItem(CURRENT_ENTITY_KEY)
    }
  }

  static getCurrentSession(): null | UserSession {
    const role = sessionStorage.getItem(CURRENCT_ROLE_KEY) as SessionRole | null
    const entity_id = sessionStorage.getItem(CURRENT_ENTITY_KEY)

    if (!role) {
      return null
    }

    if (!isMultiEntityRole(role) && !entity_id) {
      return null
    }

    return {
      role,
      entity_id,
    }
  }

  static setLastSession(role: SessionRole, entity_id?: string | null) {
    localStorage.setItem(LAST_VISIBLE_ROLE_KEY, role)
    if (entity_id) {
      localStorage.setItem(LAST_VISIBLE_ENTITY_KEY, entity_id)
    } else {
      localStorage.removeItem(LAST_VISIBLE_ENTITY_KEY)
    }
  }

  static getLastSession(): null | UserSession {
    const role = localStorage.getItem(
      LAST_VISIBLE_ROLE_KEY
    ) as SessionRole | null
    const entity_id = localStorage.getItem(LAST_VISIBLE_ENTITY_KEY)

    if (!role) {
      return null
    }

    if (!isMultiEntityRole(role) && !entity_id) {
      return null
    }

    return {
      role,
      entity_id,
    }
  }

  static setLastAssistantClient(clientRelationId: string) {
    localStorage.setItem(LAST_ASSISTANT_CLIENT_KEY, clientRelationId)
  }

  static getLastAssistantClient(): string | null {
    return localStorage.getItem(LAST_ASSISTANT_CLIENT_KEY)
  }

  static saveAuthErrors(error: string[]) {
    sessionStorage.setItem(AUTH_ERROR_KEY, JSON.stringify(error))
  }

  static getAuthErrors(): string[] {
    const errMessage = sessionStorage.getItem(AUTH_ERROR_KEY)
    if (!errMessage) {
      return []
    }

    sessionStorage.removeItem(AUTH_ERROR_KEY)
    return JSON.parse(errMessage)
  }
}

ApiService.setSessionHandlers({
  refreshToken: () => UserService.refreshToken(),
  expireSession: () => UserService.logout('?expired=true', true, false),
})

export default UserService
